import React, { useState } from 'react';
import FeedCard from 'components/feed-card';
import { MealType } from 'utils/types';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ReactComponent as ArrowBackIcon } from 'assets/icons/arrow-back.svg';
import { ReactComponent as EditMealIcon } from 'assets/icons/edit-meal-icon.svg';
import { ReactComponent as DeleteMealIcon } from 'assets/icons/delete-meal-icon.svg';
import { ReactComponent as DuplicateMealIcon } from 'assets/icons/duplicate-meal-icon.svg';
import { ReactComponent as ConvertMealIcon } from 'assets/icons/convert-meal-icon.svg';
import MealDropdownOptions, { MealDropdownOptionType } from 'components/meal-dropdown-options';
import { Link, useNavigate } from 'react-router-dom';
import { numberWithCommas } from 'utils/functions';
import DeleteModal from 'components/base/delete-modal';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { ROUTE_KEYS, ROUTE_PREFIX } from 'utils/constants';
import ChowdeckIcon from 'assets/images/chowdeck.png';
import useDashboardContext from 'contexts/dashboard.context';

type Props = {
  meal: MealType;
  activeMeal: MealType;
  setActiveMeal: (meal: MealType) => void;
  onDeleteMeal: (meal_id: string) => void;
};
const Meal = ({ meal, onDeleteMeal }: Props) => {
  const [showMoveToChowdeckModal, setShowMoveToChowdeckModal] = useState(false);
  const [activeMeal, setActiveMeal] = useState<MealType>({} as MealType);
  const { routePrefix } = useDashboardContext();
  const navigate = useNavigate();
  const [showDeleteMealModal, setshowDeleteMealModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const deleteMeal = () => {
    setshowDeleteMealModal(!showDeleteMealModal);
  };
  const editMeal = () => {
    navigate(`${ROUTE_PREFIX.FOOD}/edit-meal/${activeMeal?.id}`);
  };
  const convertMeal = () => {
    navigate(`${ROUTE_PREFIX.FOOD}/convert-meal-to-addon/${activeMeal?.id}`, {
      state: activeMeal?.name
    });
  };
  const duplicateMeal = () => {
    navigate(`${ROUTE_PREFIX.FOOD}/duplicate-meal/${activeMeal?.id}`, {
      state: activeMeal?.name
    });
  };
  const onMoveToChowdeck = async () => {
    try {
      await apiService.post(`brand-partnership/sync-brand-meals-to-chowdeck`, {
        data: { brandId: activeMeal?.brand?.id }
      });
      toast(<ToastMessage type='success' message='Meal synced successfully' />);
      setShowMoveToChowdeckModal(false);
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  const dropdownList: MealDropdownOptionType[] = [
    {
      name: 'Edit meal',
      icon: <EditMealIcon className='w-4 h-4' />,
      onClick: editMeal
    },
    {
      name: 'Move to Chowdeck',
      icon: <img src={ChowdeckIcon} alt='Chowdeck Icon' className='w-4 h-4' />,
      onClick: () => setShowMoveToChowdeckModal(true),
      className: `${activeMeal?.brand?.is_private ? '' : 'hidden'}`
    },
    {
      name: 'Duplicate meal',
      icon: <DuplicateMealIcon className='w-4 h-4' />,
      onClick: duplicateMeal
    },
    {
      name: 'Convert meal',
      icon: <ConvertMealIcon className='w-4 h-4' />,
      onClick: convertMeal
    },
    {
      name: 'Archive meal',
      icon: <DeleteMealIcon className='w-4 h-4 text-red-5' />,
      className: 'text-red-5',
      onClick: deleteMeal
    }
  ];
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: meal.id
  });

  const style = {
    transition,
    transform: CSS.Transform.toString(transform)
  };
  const handleDeleteMeal = async () => {
    setLoading(true);
    try {
      const res = await apiService.delete(`meal/marketing/meal/${meal.id}`);
      if (res) {
        toast(<ToastMessage type='success' message='Meal archived successfully' />);
        onDeleteMeal(meal.id);
        setshowDeleteMealModal(false);
      }
    } catch {
      toast(<ToastMessage type='error' message='Error encountered while deleting meal' />);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div key={meal.id} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <FeedCard
        className='w-full'
        image={meal.images[0]}
        actions={
          <MealDropdownOptions onClick={() => setActiveMeal(meal)} dropdownList={dropdownList} />
        }>
        <Link to={`${routePrefix}/${ROUTE_KEYS.MEALS}/${meal.id}/addon-categories`}>
          <div className='flex justify-between items-center mt-2'>
            <div>
              <p className='text-sm'>{meal?.base_meal?.name}</p>
              <p className='text-xs text-gray-5'>&#8358;{numberWithCommas(meal.amount)}</p>
            </div>
            <ArrowBackIcon className='transform rotate-180 w-[20px] h-[20px] text-[#B6AFAF]' />
          </div>
        </Link>
      </FeedCard>
      <DeleteModal
        show={showDeleteMealModal}
        onClose={() => setshowDeleteMealModal(false)}
        handleDelete={handleDeleteMeal}
        description='This action cannot be undone'
        title={`Are you sure you want to archive ${meal.name} ?`}
        loading={loading}
      />
      <DeleteModal
        onClose={() => setShowMoveToChowdeckModal(false)}
        show={showMoveToChowdeckModal}
        description={`Are you sure you want to move ${activeMeal?.name} to chowdeck `}
        handleDelete={onMoveToChowdeck}
        loading={loading}
        title={`Move ${activeMeal.name} to chowdeck ?`}
        type='move'
      />
    </div>
  );
};

export default Meal;
